import React, {useCallback} from 'react';

import {License, LicenseOrdering, ProductType} from '@br/idm-api';
import {DataTable, useLicenseTransferContext} from '@br/idm-components';
import {withProvider} from '@br/mg-components';

import {useRefreshableTable} from '../../contexts';
import {
    PaginatedRequest,
    parseBool,
    parseDate,
    parseFilters,
    parseMultiString,
    parseStrEnum,
    parseString,
} from '../../util';

import {LicenseTransferContextProvider, TransferLicenseModal} from './TransferLicenseModal';
import {useLicenseColumns, useMapLicenseData} from './utils';

export type LicenseFilters = {
    product?: string[];
    productSku?: string[];
    productType?: ProductType;
    licenseNumber?: string;
    expiryDateAfter?: Date;
    expiryDateBefore?: Date;
    activationDateAfter?: Date;
    activationDataBefore?: Date;
    isSubscription?: boolean;
    autoRenew?: boolean;
};

export function loadLicenseFilters(/* istanbul ignore next */ defaults: LicenseFilters = {}): LicenseFilters {
    return parseFilters(
        new URLSearchParams(location.search),
        {
            product: parseMultiString,
            productSku: parseMultiString,
            productType: parseStrEnum(ProductType),
            licenseNumber: parseString,
            expiryDateAfter: parseDate,
            expiryDateBefore: parseDate,
            activationDateAfter: parseDate,
            activationDataBefore: parseDate,
            isSubscription: parseBool,
            autoRenew: parseBool,
        },
        defaults
    );
}

export type PaginatedLicenseRequest = PaginatedRequest<LicenseFilters, LicenseOrdering, License>;

const LicenseTable = ({fetchLicenses}: {fetchLicenses: PaginatedLicenseRequest}): JSX.Element => {
    const tableRef = useRefreshableTable();
    const {targetLicense, setTargetLicense} = useLicenseTransferContext();
    const handleTransferModalOnClose = useCallback(() => {
        setTargetLicense(undefined);
        tableRef?.ref?.current?.refresh();
    }, [tableRef?.ref, setTargetLicense]);

    const initialPreferences = {
        sortBy: {id: 'activationDate', type: 'descending'} as const,
        width: [
            {columnId: 'activationDate', minWidth: 450},
            {columnId: 'expiryDate', minWidth: 450},
        ],
        defaultOrder: [
            'product',
            'productType',
            'licenseNumber',
            'deviceCount',
            'seatsInUse',
            'activationDate',
            'expiryDate',
            'isSubscription',
            'autoRenew',
        ],
    };

    return (
        <DataTable
            data-testid="license-table"
            fetch={fetchLicenses}
            useMapData={useMapLicenseData}
            useColumns={useLicenseColumns}
            loadFilters={loadLicenseFilters}
            initialPreferences={initialPreferences}
            actionArea={
                <TransferLicenseModal
                    onClose={handleTransferModalOnClose}
                    targetLicense={targetLicense}
                />
            }
        />
    );
};

export default withProvider(LicenseTransferContextProvider, LicenseTable);
